import * as tus from "tus-js-client";
import { getToken, refreshToken } from "@/scripts/keycloak.js";

// Big enough that a multi-gigabyte file is not thousands of requests, small enough to resume near
// where the connection dropped.
const CHUNK_SIZE = 50 * 1024 * 1024;

// The first delay also leaves room for a refreshed token to land before the retry goes out.
const RETRY_DELAYS = [1000, 3000, 10000, 30000];

function statusOf(error) {
  const response = error && error.originalResponse;
  return (response && response.getStatus()) || 0;
}

/** The endpoint refuses in plain text ("Required: Title", "Requires EDIT…"); show that, not a code. */
function messageOf(error) {
  const response = error && error.originalResponse;
  const body = response && response.getBody();
  return (body && String(body).trim()) || (error && error.message) || "Upload failed";
}

/**
 * Starts (or resumes) one resumable upload. The fingerprint tus keeps in localStorage is what
 * makes picking the same file again after a reload continue instead of starting a second asset.
 */
export function startUpload(file, target, callbacks) {
  const upload = new tus.Upload(file, {
    endpoint: process.env.VUE_APP_API_ENDPOINT + "/v1/upload/tus",
    chunkSize: CHUNK_SIZE,
    retryDelays: RETRY_DELAYS,
    removeFingerprintOnSuccess: true,
    metadata: {
      filename: file.name,
      filetype: file.type,
      parentID: target.parentID || "",
      metadata: JSON.stringify(target.metadata || []),
    },
    // An upload outlives its access token, so every request re-reads the current one.
    onBeforeRequest: (request) => request.setHeader("Authorization", "Bearer " + getToken()),
    onShouldRetry: (error) => {
      const status = statusOf(error);
      if (status === 401) {
        refreshToken();
        return true;
      }
      // A 4xx is the server saying no; only its own faults and a dead line are worth retrying.
      return status === 0 || status >= 500;
    },
    onProgress: callbacks.onProgress,
    onSuccess: callbacks.onSuccess,
    onError: (error) => callbacks.onError(messageOf(error)),
  });

  upload.findPreviousUploads().then((previous) => {
    if (previous.length) upload.resumeFromPreviousUpload(previous[0]);
    upload.start();
  });
  return upload;
}
